import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  hasSubdomainClientId,
  extractClientIdFromSubdomain,
} from './useClientId';
import { useDashboardNavigate } from './useDashboardNavigate';
import { buildClientPrefixedPath } from '../utils/url';

// Regex patterns used for path cleaning and validation
const REGEX_SPECIAL_CHARS_ESCAPE = /[.*+?^${}()|[\]\\]/g;
const MULTIPLE_SLASHES_PATTERN = /\/+/g;
const TRAILING_SLASH_PATTERN = /\/$/;
const ANY_CLIENT_ID_SEGMENT_PATTERN = /\/clientId\/[^/]+(\/|$)/g;
const CLIENT_ID_SEGMENT_VALUE_PATTERN = /\/clientId\/([^/]+)/g;

/**
 * Escapes special regex characters in a string for safe use in regex patterns.
 */
function escapeRegexChars(str: string): string {
  return str.replace(REGEX_SPECIAL_CHARS_ESCAPE, '\\$&');
}

/**
 * Creates a regex pattern to match /clientId/${clientId} segments in a pathname.
 */
function createClientIdSegmentPattern(clientId: string): RegExp {
  const escapedClientId = escapeRegexChars(clientId);
  return new RegExp(`/clientId/${escapedClientId}(/|$)`, 'g');
}

/**
 * Creates a regex pattern to match all occurrences of /clientId/${clientId} for duplicate detection.
 */
function createDuplicateClientIdPattern(clientId: string): RegExp {
  const escapedClientId = escapeRegexChars(clientId);
  return new RegExp(`/clientId/${escapedClientId}`, 'g');
}

/**
 * Removes ALL occurrences of /clientId/${clientId} segments from a path.
 * This prevents infinite loops when paths contain multiple clientId segments.
 */
function removeAllClientIdSegments(pathname: string, clientId: string): string {
  // Remove all occurrences of /clientId/${clientId} (with optional trailing slash)
  const clientIdSegmentPattern = createClientIdSegmentPattern(clientId);
  let cleaned = pathname.replace(clientIdSegmentPattern, '/');
  // Normalize multiple consecutive slashes and ensure path starts with /
  cleaned = cleaned.replace(MULTIPLE_SLASHES_PATTERN, '/');
  if (!cleaned.startsWith('/')) {
    cleaned = `/${cleaned}`;
  }
  // If we removed everything, return root
  return cleaned === '/'
    ? '/'
    : cleaned.replace(TRAILING_SLASH_PATTERN, '') || '/';
}

/**
 * Removes ALL /clientId/* segments from a path, regardless of the clientId value.
 * This is used to clean paths that may contain any clientId segment before rebuilding.
 * Iterates until no more matches are found to handle cases with multiple clientId segments.
 */
function removeAllClientIdSegmentsGeneric(pathname: string): string {
  // Match any /clientId/{anything} segment
  // Iterate until no more matches are found (handles multiple consecutive clientId segments)
  let cleaned = pathname;
  let prevCleaned = '';
  while (cleaned !== prevCleaned) {
    prevCleaned = cleaned;
    cleaned = cleaned.replace(ANY_CLIENT_ID_SEGMENT_PATTERN, '/');
  }
  // Normalize multiple consecutive slashes and ensure path starts with /
  cleaned = cleaned.replace(MULTIPLE_SLASHES_PATTERN, '/');
  if (!cleaned.startsWith('/')) {
    cleaned = `/${cleaned}`;
  }
  // If we removed everything, return root
  return cleaned === '/'
    ? '/'
    : cleaned.replace(TRAILING_SLASH_PATTERN, '') || '/';
}

/**
 * True when the pathname contains more than one /clientId/{subdomainClientId} segment.
 */
function hasDuplicateClientIdSegments(
  pathname: string,
  subdomainClientId: string
): boolean {
  const duplicateClientIdSegmentPattern =
    createDuplicateClientIdPattern(subdomainClientId);
  const segmentMatches = pathname.match(duplicateClientIdSegmentPattern);
  return Boolean(segmentMatches && segmentMatches.length > 1);
}

/**
 * True when the pathname contains a /clientId/{value} segment whose value
 * differs from subdomainClientId — i.e. some other/wrong clientId is present
 * anywhere in the path, not just as the leading segment
 * (e.g. /clientId/acme/clientId/other/entities on the acme subdomain).
 */
function hasForeignClientIdSegment(
  pathname: string,
  subdomainClientId: string
): boolean {
  for (const match of pathname.matchAll(CLIENT_ID_SEGMENT_VALUE_PATTERN)) {
    if (match[1] !== subdomainClientId) {
      return true;
    }
  }
  return false;
}

/**
 * True when the pathname already starts with the expected clientId prefix,
 * has no duplicate segments, and has no other/wrong clientId segment further
 * down the path — i.e. no redirect is needed.
 */
function isPathAlreadyCorrect(
  pathname: string,
  expectedPrefix: string,
  subdomainClientId: string,
  hasDuplicateSegments: boolean
): boolean {
  if (
    hasDuplicateSegments ||
    hasForeignClientIdSegment(pathname, subdomainClientId)
  ) {
    return false;
  }
  return (
    pathname === expectedPrefix || pathname.startsWith(`${expectedPrefix}/`)
  );
}

/**
 * Strips wrong/duplicate /clientId/* segments from the pathname so it can be
 * rebuilt with the correct subdomain clientId.
 *
 * - If the route clientId differs from the subdomain clientId, or the path
 *   contains some other clientId segment entirely, remove every clientId
 *   segment generically (handles multiple distinct wrong values).
 * - Otherwise just remove duplicate occurrences of the subdomain clientId.
 */
function cleanPathForRedirect(
  pathname: string,
  routeClientId: string | undefined,
  subdomainClientId: string
): string {
  const hasWrongClientId =
    (routeClientId && routeClientId !== subdomainClientId) ||
    hasForeignClientIdSegment(pathname, subdomainClientId);

  if (hasWrongClientId) {
    return removeAllClientIdSegmentsGeneric(pathname);
  }
  return removeAllClientIdSegments(pathname, subdomainClientId);
}

/**
 * Hook that corrects the URL for new URL structure when the route clientId
 * differs from the subdomain-extracted clientId.
 *
 * For new URL structure (dashboard.{client}.{stack}.{domain}), the subdomain
 * clientId is the source of truth. If the URL path contains a different
 * clientId, this hook redirects to the correct URL.
 *
 * This only applies to the new URL structure. Legacy URLs are not affected.
 */
export function useClientIdUrlCorrection(): void {
  const navigate = useDashboardNavigate();
  const location = useLocation();
  const params = useParams();
  const routeClientId = (params as Record<string, string | undefined>)
    ?.clientId;

  useEffect(() => {
    const hostname = window.location.hostname;
    const hasSubdomain = hasSubdomainClientId(hostname);

    if (hasSubdomain) {
      const subdomainClientId = extractClientIdFromSubdomain(hostname);

      if (!subdomainClientId) {
        return;
      }

      const expectedPrefix = `/clientId/${subdomainClientId}`;

      // Check if path has duplicate clientId segments (indicates corrupted path)
      const hasDuplicateSegments = hasDuplicateClientIdSegments(
        location.pathname,
        subdomainClientId
      );

      // If path already starts with correct prefix AND has no duplicates, skip redirect
      if (
        isPathAlreadyCorrect(
          location.pathname,
          expectedPrefix,
          subdomainClientId,
          hasDuplicateSegments
        )
      ) {
        return;
      }

      // If we have duplicate segments or incorrect clientId, clean up the path
      const pathToClean = cleanPathForRedirect(
        location.pathname,
        routeClientId,
        subdomainClientId
      );

      // Build the correct path with subdomain clientId
      const correctPath = buildClientPrefixedPath(
        pathToClean,
        subdomainClientId
      );

      // Safety check: Prevent redirect if resulting path would be same as current
      if (correctPath === location.pathname) {
        return;
      }

      // The search is named on the destination, not left to the merge: this
      // is a correction of the SAME page, so page-scoped `p.*` params
      // (which the merge drops from other destinations, DP-6873) must
      // survive it — the correction never costs the caller their params.
      navigate(
        { pathname: correctPath, search: location.search },
        { replace: true }
      );
    }
  }, [navigate, location.pathname, location.search, routeClientId]);
}
